
<template>
  <v-expansion-panels accordion>
    <v-expansion-panel :disabled="!actvalid">
      <v-expansion-panel-title>
        <div v-show="actvalid" class="heading">Voraussichtliche Ergebnisse</div>
        <div v-show="!actvalid" class="heading">
          Die Vorschau ist nur bei gültigen Parametern verfügbar!
        </div>
      </v-expansion-panel-title>

      <v-expansion-panel-text>
        <!-- Error state -->
        <v-alert
          v-if="plotErrors"
          type="error"
          variant="tonal"
          class="mb-2"
          :text="plotErrors.detail ?? 'Fehler beim Laden der Vorschau.'"
        />

        <v-tabs
          v-model="slide"
          align-tabs="center"
          density="comfortable"
          class="mb-2"
          grow
        >
          <v-tab :value="0" prepend-icon="mdi-chart-bar">Absolut</v-tab>
          <v-tab :value="1" prepend-icon="mdi-percent">Relativ</v-tab>
          <v-tab :value="2" prepend-icon="mdi-compare-horizontal">Vorher/Nachher</v-tab>
          <v-tab v-if="hasCo2" :value="3" prepend-icon="mdi-cloud">CO₂</v-tab>
          <v-tab v-if="hasCost" :value="4" prepend-icon="mdi-currency-eur">Kosten</v-tab>
          <v-tab :value="5" prepend-icon="mdi-cash-clock">Amortisation</v-tab>
        </v-tabs>

        <v-window v-model="slide" :transition="false" :reverse-transition="false">
          <v-window-item :value="0" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewChart
                v-show="actvalid"
                :rows="data.delta"
                title="Jährliche Einsparung absolut"
                unit="kWh/a"
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>

          <v-window-item :value="1" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewChart
                v-show="actvalid"
                :rows="data.delta_rel"
                title="Differenz relativ"
                unit="%"
                stacked
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>

          <v-window-item :value="2" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewChart
                v-show="actvalid"
                :series="beforeAfterSeries"
                title="Energie Vorher/Nachher"
                unit="kWh/a"
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>

          <v-window-item v-if="hasCo2" :value="3" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewChart
                v-show="actvalid"
                :rows="data.co2_delta"
                title="CO₂-Einsparung"
                unit="kg CO₂/a"
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>

          <v-window-item v-if="hasCost" :value="4" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewChart
                v-show="actvalid"
                :rows="data.cost_delta"
                title="Energiekosten-Einsparung"
                unit="€/a"
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>

          <v-window-item :value="5" eager>
            <v-row class="fill-height" align="center" justify="center">
              <PreviewAmortisation
                v-show="actvalid"
                :economics="data.economics"
                :loading="loading"
                :project-name="projectName"
                :method-name="methodName"
              />
            </v-row>
          </v-window-item>
        </v-window>

        <v-alert
          v-show="actvalid"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-3"
        >
          <strong>Hinweis:</strong> Die Diagramme stellen Endenergien dar.
          Einsparungen werden positiv dargestellt. Negative Werte stellen daher
          Mehrverbräuche dar.
        </v-alert>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/plugins/axios'
import '@/assets/StyleMethodHelp.css'
import PreviewChart from './PreviewChart.vue'
import PreviewAmortisation from './PreviewAmortisation.vue'

const props = defineProps({
  actdata:     { type: Object, required: true },
  actvalid:    { type: Boolean, default: false },
  projectName: { type: String, default: '' },
  methodName:  { type: String, default: '' },
})

const slide = ref(0)
const loading = ref(true)
const plotErrors = ref(null)
function emptyData() {
  return {
    reference: [],
    planned: [],
    delta: [],
    delta_rel: [],
    co2_delta: [],
    cost_delta: [],
    economics: null,
  }
}

const data = ref(emptyData())

const hasCo2 = computed(() => (data.value.co2_delta?.length ?? 0) > 0)
const hasCost = computed(() => (data.value.cost_delta?.length ?? 0) > 0)

// Consumed energy comes back negative; flip the sign so the Vorher/Nachher
// bars read as positive consumption.
function positiveRows(rows) {
  return (rows ?? []).map((r) => ({ ...r, quantity: -r.quantity }))
}

const beforeAfterSeries = computed(() => [
  { label: 'Vorher (Referenz)', rows: positiveRows(data.value.reference) },
  { label: 'Nachher (Geplant)', rows: positiveRows(data.value.planned) },
])

onMounted(() => {
  if (props.actvalid) loadData()
})

watch(
  () => props.actvalid,
  (value) => {
    if (value) loadData()
  },
)

watch(
  () => props.actdata,
  () => {
    if (props.actvalid) loadData()
  },
  { deep: true },
)

async function loadData() {
  loading.value = true
  plotErrors.value = null
  try {
    const response = await api.post('/actions/preview/', props.actdata)
    data.value = { ...emptyData(), ...response.data }
  } catch (error) {
    plotErrors.value = error?.response?.data ?? { detail: 'Fehler beim Laden der Vorschau.' }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.markdown-body {
  box-sizing: border-box;
  min-width: 200px;
  margin: 0 auto;
  padding: 45px;
  max-width: 80ch;
}

@media (max-width: 767px) {
  .markdown-body {
    padding: 15px;
  }
}

div.heading {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.25rem;
}
</style>
